import JSONRPC from './JSONRPC';
import loginActions from 'js/redux/reducers/Login';
import standalone2cloudActions from 'js/redux/reducers/Standalone2Cloud';
import log from './Logger';
import { getApiBaseUrl, getToken } from './WebRequest';
import type { JsonRpcTransport } from './types/JSONRPCAPI';
import type {
  Standalone2CloudBrowserContext,
  Standalone2CloudConnectionParameters,
  Standalone2CloudCancelSyncResponse,
  Standalone2CloudPort,
  Standalone2CloudQtObject,
  Standalone2CloudSite,
  Standalone2CloudSiteParameters,
  Standalone2CloudStartSyncResponse,
  Standalone2CloudStore,
  Standalone2CloudSyncPayload,
} from './types/Standalone2Cloud';

export type {
  Standalone2CloudBrowserContext,
  Standalone2CloudConnectionParameters,
  Standalone2CloudCancelSyncResponse,
  Standalone2CloudPort,
  Standalone2CloudQtObject,
  Standalone2CloudSite,
  Standalone2CloudSiteParameters,
  Standalone2CloudStartSyncResponse,
  Standalone2CloudStore,
  Standalone2CloudSyncPayload,
} from './types/Standalone2Cloud';

function createNullJsonRpc(): JsonRpcTransport {
  return {
    request: <T = unknown>() => Promise.resolve(null as T),
    handleResponse: () => undefined,
  };
}

const AUTHENTICATION_REFRESH_WAIT_MS = 5000;
const AUTHENTICATION_REFRESH_POLL_MS = 100;

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

function logErrorWithContext(message: string, error: unknown): void {
  log.error(message);
  log.error(error);
}

async function refreshAuthentication(
  store: Standalone2CloudStore
): Promise<void> {
  const previousToken = getToken();
  log.info(
    `Order-sync authentication refresh requested: token_available=${Boolean(
      previousToken
    )}`
  );
  store.dispatch(loginActions.refreshToken());
  const deadline = Date.now() + AUTHENTICATION_REFRESH_WAIT_MS;

  while (getToken() === previousToken && Date.now() < deadline) {
    await delay(AUTHENTICATION_REFRESH_POLL_MS);
  }

  const refreshedToken = getToken();
  log.info(
    `Order-sync authentication refresh completed: token_changed=${
      refreshedToken !== previousToken
    } token_available=${Boolean(refreshedToken)}`
  );
}

class Standalone2CloudAPI {
  private jsonrpc: JsonRpcTransport;

  constructor() {
    this.jsonrpc = createNullJsonRpc();
  }

  connect(
    qtobject: Standalone2CloudQtObject | null,
    store: Standalone2CloudStore
  ): void {
    if (!qtobject) {
      return;
    }
    try {
      this.jsonrpc = new JSONRPC((message) =>
        qtobject.dispatch(JSON.stringify(message))
      );
      qtobject.response.connect((str: string) =>
        this.jsonrpc.handleResponse(JSON.parse(str))
      );

      qtobject.progress.connect((message: string, percent: number) =>
        store.dispatch(
          standalone2cloudActions.progressUpdated(message, percent)
        )
      );
      qtobject.sync_started.connect(() =>
        store.dispatch(standalone2cloudActions.syncStarted())
      );
      qtobject.sync_finished.connect((success: boolean, info: string) =>
        store.dispatch(standalone2cloudActions.syncFinished(success, info))
      );
      qtobject.sync_cancelled.connect(() =>
        store.dispatch(standalone2cloudActions.syncCancelled())
      );

      if (qtobject.browser_context_requested) {
        log.info('Order-sync browser context signal is available');
        qtobject.browser_context_requested.connect(
          (requestId: number, shouldRefreshAuthentication: boolean) => {
            log.info(
              `Order-sync browser context request received: request_id=${requestId} refresh_authentication=${shouldRefreshAuthentication}`
            );
            void this.provideBrowserContext(
              requestId,
              shouldRefreshAuthentication,
              store
            );
          }
        );
        void this.jsonrpc
          .request('register_browser_context_provider')
          .then(() =>
            log.info('Order-sync browser context provider registered')
          )
          .catch((error: unknown) => {
            logErrorWithContext(
              'Unable to register order-sync browser context',
              error
            );
          });
      }
    } catch (error) {
      // Fallback to dummy jsonrpc if connection fails
      this.jsonrpc = createNullJsonRpc();
      log.error(error);
    }
  }

  private async provideBrowserContext(
    requestId: number,
    shouldRefreshAuthentication: boolean,
    store: Standalone2CloudStore
  ): Promise<void> {
    let context: Standalone2CloudBrowserContext | null = null;
    try {
      if (shouldRefreshAuthentication) {
        await refreshAuthentication(store);
      }
      context = {
        base_url: getApiBaseUrl(),
        token: getToken(),
      };
      log.info(
        `Order-sync browser context resolved: request_id=${requestId} base_url=${context.base_url} token_available=${Boolean(
          context.token
        )}`
      );
    } catch (error) {
      logErrorWithContext('Unable to read order-sync browser context', error);
    }
    try {
      const response = await this.jsonrpc.request<{ accepted: boolean }>(
        'provide_browser_context',
        [requestId, context]
      );
      log.info(
        `Order-sync browser context response delivered: request_id=${requestId} accepted=${response.accepted}`
      );
    } catch (error) {
      logErrorWithContext(
        'Unable to provide order-sync browser context',
        error
      );
    }
  }

  _normalizePort(port: Standalone2CloudPort): number {
    const parsed = Number(port);
    return Number.isInteger(parsed) ? parsed : 3306;
  }

  listDatabases(
    host: Standalone2CloudConnectionParameters['host'],
    port: Standalone2CloudConnectionParameters['port'],
    user: Standalone2CloudConnectionParameters['user'],
    password: Standalone2CloudConnectionParameters['password']
  ): Promise<string[]> {
    return this.jsonrpc.request<string[]>('list_databases', [
      host,
      this._normalizePort(port),
      user,
      password,
    ]);
  }

  listSites(
    host: Standalone2CloudSiteParameters['host'],
    port: Standalone2CloudSiteParameters['port'],
    user: Standalone2CloudSiteParameters['user'],
    password: Standalone2CloudSiteParameters['password'],
    database: Standalone2CloudSiteParameters['database']
  ): Promise<Standalone2CloudSite[]> {
    return this.jsonrpc.request<Standalone2CloudSite[]>('list_sites', [
      host,
      this._normalizePort(port),
      user,
      password,
      database,
    ]);
  }

  startSync(
    payload: Standalone2CloudSyncPayload
  ): Promise<Standalone2CloudStartSyncResponse> {
    const {
      host,
      port,
      user,
      password,
      database,
      site_id,
      upload_site_id,
      timeout,
    } = payload;
    return this.jsonrpc.request<Standalone2CloudStartSyncResponse>(
      'start_sync',
      [
        host,
        this._normalizePort(port),
        user,
        password,
        database,
        site_id,
        upload_site_id,
        timeout,
      ]
    );
  }

  cancelSync(): Promise<Standalone2CloudCancelSyncResponse> {
    return this.jsonrpc.request<Standalone2CloudCancelSyncResponse>(
      'cancel_sync'
    );
  }
}

const api = new Standalone2CloudAPI();
export default api;
